import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { CaretDown } from '@phosphor-icons/react';
import type { FlowRunNode } from '../../types/flow-execution.types';
import NodeOutputPanel from './panels/NodeOutputPanel';

interface Props {
  node: FlowRunNode;
  expanded: boolean;
  onToggle: (id: string) => void;
}

const NODE_TYPE_LABELS: Record<string, string> = {
  commandTextNode: 'Text Generator',
  outputController: 'Display Box',
};

function getDisplayLabel(node: FlowRunNode): string {
  if (node.label && node.label.trim().length > 0) return node.label;
  if (node.type && NODE_TYPE_LABELS[node.type]) {
    return NODE_TYPE_LABELS[node.type];
  }
  return node.type ?? 'Node';
}

function NodeRow({ node, expanded, onToggle }: Props) {
  const isExpandable = !node.isRetentionSlimmed;
  const displayLabel = getDisplayLabel(node);
  return (
    <Accordion
      expanded={expanded && isExpandable}
      onChange={() => {
        if (!isExpandable) return;
        onToggle(node.id);
      }}
      disableGutters
      square
      sx={{ '&:before': { display: 'none' } }}
    >
      <AccordionSummary
        expandIcon={isExpandable ? <CaretDown size={16} /> : null}
        sx={{
          cursor: isExpandable ? 'pointer' : 'default',
          opacity: isExpandable ? 1 : 0.7,
        }}
      >
        <Stack
          direction='row'
          spacing={1}
          alignItems='center'
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Chip
            size='small'
            variant='outlined'
            label={node.isError ? 'Failed' : 'Completed'}
            color={node.isError ? 'error' : 'success'}
          />
          <Typography
            variant='body2'
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayLabel}
          </Typography>
          {node.isRetentionSlimmed && (
            <Typography variant='caption' color='text.secondary'>
              I/O expired by retention
            </Typography>
          )}
          {typeof node.usedTokens === 'number' && (
            <Chip
              size='small'
              variant='outlined'
              label={`${Math.round(node.usedTokens * 1000) / 1000} tok`}
            />
          )}
        </Stack>
      </AccordionSummary>
      {isExpandable && (
        <AccordionDetails>
          {expanded && <NodeOutputPanel id={node.id} nodeType={node.type} />}
        </AccordionDetails>
      )}
    </Accordion>
  );
}

export default NodeRow;
